import React from "react";
import { Chat } from "../../types/conversations";
import utils from "../../helpers/utils";

interface ChatListItemProps {
  chat: Chat;
  isSelected: boolean;
  onClick: () => void;
  /** Se true, o usuário logado é profissional (mostra "Aluno"); se false, é aluno (mostra "Meu professor") */
  viewerIsProfessional?: boolean;
}

/** Formata o horário da última mensagem: hoje → HH:mm, ontem → "Ontem", mais antigo → dd/mm/yyyy */
function formatLastMessageTime(dateStr: string): string {
  if (!dateStr?.trim()) return "-";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  const now = new Date();
  const hoje =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  const ontem = new Date(now);
  ontem.setDate(ontem.getDate() - 1);
  const foiOntem =
    date.getDate() === ontem.getDate() &&
    date.getMonth() === ontem.getMonth() &&
    date.getFullYear() === ontem.getFullYear();
  if (hoje) return utils.formatTime(date);
  if (foiOntem) return "Ontem";
  return utils.formatDate(date);
}

export const ChatListItem: React.FC<ChatListItemProps> = ({
  chat,
  isSelected,
  onClick,
  viewerIsProfessional = false,
}) => {
  // Verificar se a validade está vencida (parse da data no frontend)
  const isExpired = chat.validity
    ? new Date(chat.validity) < new Date()
    : false;

  const lastMessageTimeFormatted = formatLastMessageTime(chat.lastMessageTime);

  // Determinar a URL do avatar
  const avatarUrl =
    chat.avatar && chat.userId
      ? utils.getUriImage(chat.avatar, String(chat.userId))
      : `https://ui-avatars.com/api/?name=${encodeURIComponent(
          chat.name,
        )}&background=85368c&color=fff&size=200`;

  const fallbackAvatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    chat.name,
  )}&background=85368c&color=fff&size=200`;

  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-3 cursor-pointer border-b border-gray-200 dark:border-gray-700 transition active:bg-gray-50 dark:active:bg-gray-700 md:px-4 ${
        isSelected
          ? "bg-gray-100 dark:bg-gray-700"
          : "hover:bg-gray-50 dark:hover:bg-gray-700"
      }`}
    >
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
          <img
            src={avatarUrl}
            alt={chat.name}
            className="h-full w-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = fallbackAvatarUrl;
            }}
          />
        </div>
        {chat.online && (
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-purple-400 rounded-full border-2 border-white dark:border-gray-800" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1 gap-2">
          <div className="flex items-center gap-1.5 min-w-0 flex-1 md:gap-2">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate md:text-base">
              {chat.name}
            </h3>
            {chat.isStudent && (
              <span
                className="flex-shrink-0 text-xs rounded bg-blue-500/90 text-white text-[10px] font-medium px-1.5 py-0.5 md:text-xs md:px-2"
                title={"Aluno vinculado ao profissional"}
              >
                Aluno
              </span>
            )}
            {!chat.isStudent && (
              <span
                className="flex-shrink-0 rounded bg-yellow-500/90 text-white text-[10px] font-medium px-1.5 py-0.5 md:text-xs md:px-2"
                title={"Interessado no profissional"}
              >
                Interessado
              </span>
            )}

            {isExpired && (
              <span className="bg-red-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 md:text-xs md:px-2">
                Vencido
              </span>
            )}
          </div>
          <span className="text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0 md:text-xs">
            {lastMessageTimeFormatted}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-600 dark:text-gray-400 truncate flex-1 md:text-xs">
            {chat.lastMessage}
          </p>
          {chat.unreadCount > 0 && (
            <span className="bg-purple-400 text-white text-[11px] font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center flex-shrink-0 md:text-xs md:px-2">
              {chat.unreadCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
